/**
 * EnTeteDeConversation — le haut d'une conversation ouverte : (← sur un téléphone) l'avatar, le
 * nom en Almarena, le drapeau de sa langue, sa présence en PASTILLE collée au nom (aplat vert
 * « En ligne », contour « Vu il y a 5 minutes » — Kayla, 29-10 : « pas assez visible, noyé dans
 * pas mal d'infos »), des étiquettes ; dessous une ligne grise (la créatrice, le canal,
 * l'identifiant) ; les actions à droite. Une hairline dessous, pas de bloc ; `collant` le garde en
 * haut pendant que le fil défile.
 *
 *   <EnTeteDeConversation avatar={{ src: fan.photo, graine: fan.id, enLigne: true }} nom="Max" langue="en"
 *                         presence={{ etat: "en_ligne", libelle: "En ligne" }} sousTitre="Trista · Telegram"
 *                         actions={<Button …/>} onRetour={fermer} />
 */
import type { ReactNode } from "react";

import { Button } from "../Button";
import { ArrowLeft } from "../icons";
import { cn } from "../utils";
import { DrapeauDeLangue } from "./DrapeauDeLangue";
import { AvatarDeConversation, PastilleDePresence, type AvatarDeConversationProps, type EtatDePresence } from "./Presence";
import "./conversation.css";

export interface EnTeteDeConversationProps {
  nom: ReactNode;
  /** L'avatar : l'`Avatar` de la librairie, avec sa présence et son réseau dans les coins (44 px). */
  avatar?: AvatarDeConversationProps;
  /** La langue du fan (ISO 639-1) : son drapeau à côté du nom. */
  langue?: string | null;
  /** Le pays, quand on le sait mieux que la langue. */
  pays?: string | null;
  /** « En ligne », « Vu il y a 5 minutes » — déjà écrit. Absent : rien (statut jamais relevé). */
  presence?: { etat: EtatDePresence; libelle: string } | null;
  /** Après la présence : des `Badge` (épinglé, commande en cours, tags). */
  etiquettes?: ReactNode;
  /** La ligne grise dessous : la créatrice, le canal, l'identifiant. */
  sousTitre?: ReactNode;
  /** À droite. */
  actions?: ReactNode;
  /** La flèche retour (un téléphone, une conversation ouverte par-dessus la liste). */
  onRetour?: () => void;
  /** Défaut « Retour ». */
  libelleRetour?: string;
  collant?: boolean;
  /** La balise du nom. Défaut `h2`. */
  niveau?: "h1" | "h2" | "h3";
  className?: string;
}

export function EnTeteDeConversation({
  nom,
  avatar,
  langue,
  pays,
  presence,
  etiquettes,
  sousTitre,
  actions,
  onRetour,
  libelleRetour = "Retour",
  collant = false,
  niveau: Titre = "h2",
  className,
}: EnTeteDeConversationProps) {
  return (
    <header className={cn("cv-entete", className)} data-collant={collant || undefined}>
      {onRetour && (
        <Button variant="ghost" size="lg" iconOnly className="h-10 w-10 [&_svg]:h-[22px] [&_svg]:w-[22px]" aria-label={libelleRetour} title={libelleRetour} onClick={onRetour}>
          <ArrowLeft aria-hidden />
        </Button>
      )}
      {avatar && <AvatarDeConversation nom={typeof nom === "string" ? nom : undefined} {...avatar} />}
      <div className="cv-entete-texte">
        <div className="cv-entete-ligne">
          <Titre className="cv-entete-nom">{nom}</Titre>
          <DrapeauDeLangue langue={langue} pays={pays} />
          {presence && <PastilleDePresence etat={presence.etat} libelle={presence.libelle} />}
          {etiquettes}
        </div>
        {sousTitre !== undefined && sousTitre !== null && <div className="cv-entete-sous">{sousTitre}</div>}
      </div>
      {actions && <div className="cv-entete-actions">{actions}</div>}
    </header>
  );
}
